import MainCard from 'components/MainCard';
import ProviderChip from 'components/ProviderChip';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetExternalDevicesCoverageQuery } from 'features/api/apiSlice';
import { getProviderStyle } from 'helpers/ProviderStylesHelper';
import { IGetExternalDevicesCoverageResponse } from 'models/api/responses/externaldevices/IGetExternalDevicesCoverageResponse';

type CoverageLayer = 'timeseries' | 'workout' | 'sleep' | 'womens-health' | 'scores';
type CoverageTimeseriesCategory = IGetExternalDevicesCoverageResponse['timeseries'][number];
type CoverageField = IGetExternalDevicesCoverageResponse['workout_fields'][number];

function Dot({ active }: { active: boolean }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        display: 'inline-block',
        bgcolor: active ? 'success.main' : 'grey.500',
        opacity: active ? 1 : 0.35,
      }}
    />
  );
}

function CoverageStatCard({ label, value }: { label: string; value: number }) {
  return (
    <Box
      sx={{
        borderRadius: 1.25,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'grey.100',
        px: 1.25,
        py: 0.75,
        textAlign: 'center',
        minWidth: 84,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          lineHeight: 1.1,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          fontSize: 17,
          color: value > 0 ? 'success.main' : 'text.disabled',
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          lineHeight: 1.2,
          mt: 0.25,
          fontSize: 10,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function MatrixTable({
  providers,
  rows,
}: {
  providers: string[];
  rows: { code: string; unit?: string; supportedBy: string[] }[];
}) {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {t('no_data_for_layer', 'No data for this layer.')}
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={{ textAlign: 'left', p: 1.5, borderBottom: 1, borderColor: 'divider', minWidth: 280 }} />
            {providers.map((p) => (
              <Box key={p} component="th" sx={{ textAlign: 'center', p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <ProviderChip provider={p} />
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row, i) => (
            <Box component="tr" key={row.code} sx={{ bgcolor: i % 2 === 0 ? 'action.hover' : 'transparent' }}>
              <Box component="td" sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography component="code" variant="caption" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {row.code}
                  </Typography>
                  {row.unit && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={row.unit}
                      sx={{
                        height: 20,
                        '& .MuiChip-label': {
                          px: 0.75,
                          fontSize: 10,
                          color: 'text.secondary',
                        },
                      }}
                    />
                  )}
                </Stack>
              </Box>
              {providers.map((p) => {
                const supported = row.supportedBy.includes(p);
                return (
                  <Box key={p} component="td" sx={{ textAlign: 'center', p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>
                      {supported ? 'Supported' : 'Not supported'}
                    </span>
                    <Dot active={supported} />
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ProviderDetail({ data, provider }: { data: IGetExternalDevicesCoverageResponse; provider: string }) {
  const { t } = useTranslation();
  type CoverageChip = { code: string; unit?: string };
  type CoverageCard = { title: string; chips: CoverageChip[] };

  const timeseries = data.timeseries
    .map((cat) => ({
      name: cat.name,
      metrics: cat.metrics.filter((m) => m.providers.includes(provider)),
    }))
    .filter((cat) => cat.metrics.length > 0);

  const tsCount = timeseries.reduce((acc, cat) => acc + cat.metrics.length, 0);
  const workout = data.workout_fields.filter((f) => f.providers.includes(provider));
  const sleep = data.sleep_fields.filter((f) => f.providers.includes(provider));
  const womensHealth = data.menstrual_cycle_fields.filter((f) => f.providers.includes(provider));
  const scores = data.health_scores.filter((f) => f.providers.includes(provider));

  const cards: CoverageCard[] = [
    ...timeseries.map((cat) => ({
      title: cat.name,
      chips: cat.metrics.map((m) => ({ code: m.code, unit: m.unit })),
    })),
    { title: t('workout_fields', 'Workout fields'), chips: workout.map((f) => ({ code: f.code })) },
    { title: t('sleep_fields', 'Sleep fields'), chips: sleep.map((f) => ({ code: f.code })) },
    { title: t('womens_health_fields', "Women's health fields"), chips: womensHealth.map((f) => ({ code: f.code })) },
    { title: t('health_scores', 'Health scores'), chips: scores.map((f) => ({ code: f.code })) },
  ].filter((c) => c.chips.length > 0);

  return (
    <MainCard
      contentSX={{ p: 2 }}
      title={
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ProviderChip provider={provider} />
            <Typography variant="body2" color="text.secondary">
              {t('supported_data_types', 'supported data types')}
            </Typography>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: {
                xs: 'repeat(3, minmax(0, 1fr))',
                sm: 'repeat(5, minmax(0, 1fr))',
              },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <CoverageStatCard label={t('timeseries', 'Timeseries')} value={tsCount} />
            <CoverageStatCard label={t('workout', 'Workout')} value={workout.length} />
            <CoverageStatCard label={t('sleep', 'Sleep')} value={sleep.length} />
            <CoverageStatCard label={t('womens_health', "Women's Health")} value={womensHealth.length} />
            <CoverageStatCard label={t('scores', 'Scores')} value={scores.length} />
          </Box>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Grid container spacing={1.5}>
          {cards.map((card) => (
            <Grid item xs={12} md={6} lg={4} key={card.title}>
              <MainCard contentSX={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{card.chips.length}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {card.chips.map((chip) => (
                      <Chip
                        key={`${card.title}:${chip.code}`}
                        size="small"
                        variant="outlined"
                        label={
                          <Stack direction="row" spacing={0.5} alignItems="baseline">
                            <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1 }}>
                              {chip.code}
                            </Typography>
                            {chip.unit && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 9, lineHeight: 1 }}>
                                {chip.unit}
                              </Typography>
                            )}
                          </Stack>
                        }
                        sx={{
                          height: 22,
                          fontFamily: 'monospace',
                          '& .MuiChip-label': {
                            px: 0.75,
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </MainCard>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </MainCard>
  );
}

function TimeseriesLayer({ providers, categories }: { providers: string[]; categories: CoverageTimeseriesCategory[] }) {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(categories[0]?.name ?? '');

  if (categories.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {t('no_timeseries_data', 'No timeseries data.')}
      </Typography>
    );
  }

  const selectedCategory = categories.find((x) => x.name === activeCategory) ?? categories[0];

  return (
    <Stack spacing={2}>
      <Tabs
        value={selectedCategory.name}
        onChange={(_, next) => setActiveCategory(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {categories.map((cat) => (
          <Tab key={cat.name} label={cat.name} value={cat.name} />
        ))}
      </Tabs>
      <MatrixTable
        providers={providers}
        rows={selectedCategory.metrics.map((m) => ({
          code: m.code,
          unit: m.unit,
          supportedBy: m.providers,
        }))}
      />
    </Stack>
  );
}

function GenericLayer({ providers, rows }: { providers: string[]; rows: CoverageField[] }) {
  return (
    <MatrixTable
      providers={providers}
      rows={rows.map((row) => ({
        code: row.code,
        supportedBy: row.providers,
      }))}
    />
  );
}

export default function CoveragePage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetExternalDevicesCoverageQuery();
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [activeLayer, setActiveLayer] = useState<CoverageLayer>('timeseries');

  const providers: string[] = data?.providers ?? [];

  const layerContent = useMemo(() => {
    if (!data) {
      return null;
    }

    if (activeLayer === 'timeseries') {
      return <TimeseriesLayer providers={data.providers} categories={data.timeseries} />;
    }

    if (activeLayer === 'workout') {
      return <GenericLayer providers={data.providers} rows={data.workout_fields} />;
    }

    if (activeLayer === 'sleep') {
      return <GenericLayer providers={data.providers} rows={data.sleep_fields} />;
    }

    if (activeLayer === 'womens-health') {
      return <GenericLayer providers={data.providers} rows={data.menstrual_cycle_fields} />;
    }

    return <GenericLayer providers={data.providers} rows={data.health_scores} />;
  }, [activeLayer, data]);

  return (
    <Stack spacing={2}>
      <Typography variant="body1" color="text.secondary" sx={{ mt: -2, lineHeight: 1.5 }}>
        {t('data_coverage_description', 'What each provider is capable of delivering, by API layer.')}
      </Typography>

      <Alert severity="info">
        {t(
          'data_coverage_capabilities_disclaimer',
          'This matrix shows provider capabilities: data types supported and normalized in code. It does not reflect what has already been synced for your users.'
        )}
      </Alert>

      {isLoading && (
        <MainCard>
          <Stack spacing={1.5}>
            <Grid container spacing={1.5}>
              {Array.from({ length: 10 }).map((_, idx) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={idx}>
                  <Skeleton variant="rounded" height={44} />
                </Grid>
              ))}
            </Grid>
            <Skeleton variant="rounded" height={420} />
          </Stack>
        </MainCard>
      )}

      {!isLoading && error && (
        <Alert severity="error">{t('coverage_load_error', 'Failed to load coverage data.')}</Alert>
      )}

      {!isLoading && data && (
        <>
          
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('inspect_provider', 'Inspect a provider')}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {providers.map((p) => {
              const selected = selectedProvider === p;
              const cfg = getProviderStyle(p);
              return (
                <Button
                  key={p}
                  variant="text"
                  onClick={() => setSelectedProvider(selected ? null : p)}
                  sx={{
                    minWidth: 'auto',
                    textTransform: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    border: '1.5px solid',
                    borderColor: `${alpha(cfg.text, selected ? 0.7 : 0.35)} !important`,
                    bgcolor: `${cfg.bg} !important`,
                    px: 0.75,
                    py: 0,
                    minHeight: '24px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 700,
                    lineHeight: 'normal',
                    color: `${cfg.text} !important`,
                    opacity: selected ? 1 : 0.65,
                    '&:hover': {
                      bgcolor: `${cfg.bg} !important`,
                      color: `${cfg.text} !important`,
                      borderColor: `${alpha(cfg.text, 0.7)} !important`,
                      opacity: 1,
                    },
                  }}
                >
                  {cfg.label}
                </Button>
              );
            })}
          </Stack>

          {selectedProvider && <ProviderDetail data={data} provider={selectedProvider} />}

          <MainCard content={false}>
            <Stack spacing={2}>
              <Tabs
                value={activeLayer}
                onChange={(_, next: CoverageLayer) => setActiveLayer(next)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab value="timeseries" label={t('timeseries', 'Timeseries')} />
                <Tab value="workout" label={t('workout', 'Workout')} />
                <Tab value="sleep" label={t('sleep', 'Sleep')} />
                <Tab value="womens-health" label={t('womens_health', "Women's Health")} />
                <Tab value="scores" label={t('health_scores', 'Health Scores')} />
              </Tabs>
              {layerContent}
            </Stack>
          </MainCard>
        </>
      )}
    </Stack>
  );
}
